import { Rooms } from '@features/rooms/pages/Rooms';
import { getAvailableRooms, getRoomPageSettings, getStaticRooms, roomsKeys } from '@features/rooms';
import { Property } from 'shared-types/Property';
import { NormalizedStore } from '@store/entity/entity.types';
import { NextPage, NextPageContext } from 'next';
import NextSeo from 'next-seo';
import Head from 'next/head';
import React from 'react';
import { useTranslations } from '@lang';
import { Router } from 'shared-routing';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { parseDateToString } from '@utils/helpers';

type RoomsPageProps = {
  language: string;
  url: string;
  property: Property;
};

const RoomsPage: NextPage<RoomsPageProps> = ({ language, url, property }) => {
  const { t } = useTranslations();

  const pageName = t('app.rooms');

  const pageTitle = property.propertyTitle ? `${pageName} | ${property.propertyTitle}` : pageName;
  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: property.socialFallbackImage?.url || '' }],
          },
        }}
      />
      <Rooms language={language} />
    </>
  );
};

type RoomsPageInitialProps = {
  store: NormalizedStore;
} & NextPageContext &
  Pick<RoomsPageProps, 'language' | 'url' | 'property'>;

RoomsPage.getInitialProps = async ({
  store,
  property,
  language,
  url,
  res,
  moduleStatuses,
}: RoomsPageInitialProps): Promise<RoomsPageProps> => {
  const { id: siteId } = property;
  const isServer = !!res;

  if (!moduleStatuses.roomsEnabled) {
    if (isServer) {
      res.writeHead(302, {
        Location: '/',
      });
      res.end();
    } else {
      Router.push('/');
    }
  }

  if (!isServer) {
    return {
      property,
      language,
      url,
    };
  }

  const state = store.getState();
  const journeyParams = yourJourneySelectors.getParsedJourneyParams(state);

  const adults = journeyParams.adults;
  const children = journeyParams.children;
  const childAges = journeyParams.childAges;
  const rooms = journeyParams.rooms;
  const fromDate = parseDateToString(journeyParams.fromDate);
  const toDate = parseDateToString(journeyParams.toDate);

  const client = new QueryClient();

  const roomsPageQuery = client.prefetchQuery({
    queryKey: roomsKeys.roomSettings({ siteId, language }),
    queryFn: () => getRoomPageSettings({ siteId, language }),
    cacheTime: Infinity,
    staleTime: Infinity,
  });

  const staticRoomsQuery = client.prefetchQuery({
    queryKey: roomsKeys.staticRooms({ siteId, language }),
    queryFn: () => getStaticRooms({ siteId, language }),
    cacheTime: Infinity,
    staleTime: Infinity,
  });

  const availableRoomsQuery = client.prefetchQuery({
    queryKey: roomsKeys.availableRooms({
      siteId,
      fromDate,
      toDate,
      adults,
      children,
      childAges,
      rooms,
      isMultiRoomsEnabled: rooms.length > 1,
    }),
    queryFn: () =>
      getAvailableRooms({
        siteId,
        fromDate,
        toDate,
        adults,
        children,
        childAges,
        rooms,
        isMultiRoomsEnabled: rooms.length > 1,
      }),
  });

  await Promise.all([roomsPageQuery, staticRoomsQuery, availableRoomsQuery]);

  return {
    property,
    language,
    url,
    dehydratedState: dehydrate(client),
  } as RoomsPageProps;
};

export default RoomsPage;
